export const PORTRAYAL_PACKS = {
  "enc-v0.1": {
    id: "enc-v0.1",
    label: "ENC Lookup v0.1",
    soundingTextStyle: {
      color: "#0b1220",
      haloColor: "rgba(255,255,255,0.62)",
      haloWidthPx: 1,
    },
    rules: [
      {
        id: "SOUNDG01",
        objectClass: "SOUNDG",
        viewGroup: "labels",
        priority: 900,
        match: (ctx) => ctx.objectClass === "SOUNDG",
        resolve: (ctx) => {
          const dRaw = ctx.props[ctx.cfg.depthField] ?? ctx.props.Z ?? ctx.props.VALSOU;
          const dNum = Number(dRaw);
          const text = Number.isFinite(dNum) ? dNum.toFixed(1) : "";
          return {
            style: ctx.baseStyle,
            pointRenderer: {
              kind: "label",
              text,
              color: ctx.pack?.soundingTextStyle?.color || ctx.baseStyle.color || "#000",
              haloColor: ctx.pack?.soundingTextStyle?.haloColor || "rgba(255,255,255,0.62)",
              haloWidthPx: Number.isFinite(Number(ctx.pack?.soundingTextStyle?.haloWidthPx)) ? Number(ctx.pack.soundingTextStyle.haloWidthPx) : 1,
            },
          };
        },
      },
      {
        id: "DEPARE01",
        objectClass: "DEPARE",
        viewGroup: "areas",
        priority: 200,
        match: (ctx) => ctx.objectClass === "DEPARE",
        resolve: (ctx) => {
          const d = ctx.helpers.getDepthValue(ctx.props);
          if (!Number.isFinite(d)) return { style: ctx.baseStyle };
          const b = ctx.helpers.bandForDepth(d);
          return {
            style: {
              ...ctx.baseStyle,
              color: "#245f91",
              fillColor: b?.color || ctx.baseStyle.fillColor,
              weight: Number.isFinite(Number(ctx.baseStyle.weight)) ? Number(ctx.baseStyle.weight) : 1,
            },
          };
        },
      },
      {
        id: "LIGHTS01",
        objectClass: "LIGHTS",
        viewGroup: "points",
        priority: 800,
        match: (ctx) => ctx.objectClass === "LIGHTS",
        resolve: (ctx) => {
          const primaryCode = ctx.helpers.getLightPrimaryCode(ctx.props);
          const lightColor = ctx.helpers.colorForLightCode(primaryCode) || ctx.baseStyle.color || "#fbbf24";
          const style = { ...ctx.baseStyle, color: lightColor, fillColor: lightColor };
          if (ctx.lightMode === "enc_icon") {
            const size = ctx.helpers.lightIconSize(ctx.props?.CATLIT);
            const fallbackShape = String(ctx.props?.CATLIT || "").trim() ? "circle" : "triangle";
            return {
              style,
              pointRenderer: {
                kind: "icon",
                html: ctx.helpers.lightIconHtml(ctx.props, { fallbackShape }),
                size: [size, size],
                anchor: [Math.round(size / 2), Math.round(size / 2)],
                className: "enc-light-icon",
              },
            };
          }
          return { style };
        },
      },
      {
        id: "PNTGEN01",
        objectClass: "PNTGEN",
        viewGroup: "points",
        priority: 700,
        match: (ctx) => ctx.cfg.typeHint === "point"
          && ctx.objectClass !== "SOUNDG"
          && ctx.objectClass !== "LIGHTS"
          && ctx.pointMode === "nautical",
        resolve: (ctx) => {
          const code = ctx.helpers.getLightPrimaryCode(ctx.props);
          const fill = ctx.helpers.colorForAnyCode(code, ctx.baseStyle.fillColor || ctx.baseStyle.color || "#93c5fd");
          const symbol = ctx.helpers.pointSymbolType(ctx.cfg);
          return {
            style: ctx.baseStyle,
            pointRenderer: {
              kind: "icon",
              html: ctx.helpers.pointSymbolHtml(symbol, fill),
              size: [16, 16],
              anchor: [8, 8],
              className: "enc-point-icon",
            },
          };
        },
      },
      {
        id: "DEFAULT01",
        objectClass: "GENERIC",
        viewGroup: "base",
        priority: 100,
        match: () => true,
        resolve: (ctx) => ({ style: ctx.baseStyle }),
      },
    ],
  },
};

export function registerPortrayalPack(pack) {
  if (!pack || typeof pack !== "object") throw new Error("Invalid portrayal pack");
  const id = String(pack.id || "").trim();
  if (!id) throw new Error("Portrayal pack requires an id");
  const rules = Array.isArray(pack.rules) ? pack.rules : [];
  if (!rules.length) throw new Error("Portrayal pack requires at least one rule");
  PORTRAYAL_PACKS[id] = { ...pack, id, rules };
  return id;
}

function classifyFeature(cfg) {
  const n = String(cfg?.rawName || cfg?.name || "").toLowerCase();
  if (cfg.role === "soundings") return "SOUNDG";
  if (cfg.role === "lights") return "LIGHTS";
  if (cfg.role === "depths" && cfg.typeHint === "polygon") return "DEPARE";
  if (n.includes("buoy")) return "BOY";
  if (n.includes("beacon") || n.includes("daymark") || n.includes("topmark")) return "BCN";
  if (n.includes("wreck")) return "WRECKS";
  if (n.includes("obstruction") || n.includes("rock")) return "OBSTRN";
  if (cfg.typeHint === "point") return "PNTGEN";
  if (cfg.typeHint === "line") return "LINGEN";
  if (cfg.typeHint === "polygon") return "AREGEN";
  return "UNKNOWN";
}

export function createPortrayalEngine(options) {
  const getState = options?.getState;
  const helpers = options?.helpers || {};
  const fallbackPackId = "enc-v0.1";

  function getPack() {
    const state = typeof getState === "function" ? getState() : {};
    const id = String(state?.portrayalPackId || fallbackPackId);
    return PORTRAYAL_PACKS[id] || PORTRAYAL_PACKS[fallbackPackId];
  }

  function evaluatePortrayal(cfg, feature) {
    const state = typeof getState === "function" ? getState() : {};
    const objectClass = classifyFeature(cfg);
    const pack = getPack();
    const ctx = {
      cfg,
      feature,
      props: feature?.properties || {},
      objectClass,
      baseStyle: helpers.styleForConfig(cfg),
      pointMode: state?.pointSymbologyMode || "nautical",
      lightMode: state?.lightSymbologyMode || "enc_icon",
      helpers,
      pack,
      meta: { standard: "S-101-aligned lookup scaffold" },
    };
    const rule = (pack.rules || []).find((r) => r.match(ctx)) || pack.rules[pack.rules.length - 1];
    const resolved = rule?.resolve?.(ctx) || {};
    return {
      style: resolved.style || ctx.baseStyle,
      pointRenderer: resolved.pointRenderer || null,
      ruleId: rule?.id || "DEFAULT01",
      objectClass,
      viewGroup: rule?.viewGroup || "base",
      priority: Number.isFinite(Number(rule?.priority)) ? Number(rule.priority) : 100,
      standard: ctx.meta.standard,
      packId: pack.id,
    };
  }

  return { evaluatePortrayal, getPack };
}
